const questionnaire = [
  {
    field: 'motivation',
    inputField: 'motivation',
    kind: 'single',
    values: [
      'make_money',
      'recognition',
      'entertain_friends',
      'professional_growth',
    ],
  },
  {
    field: 'starting_difficulty',
    inputField: 'startingDifficulty',
    kind: 'single',
    values: [
      'lack_of_time',
      'technical_difficulty',
      'creativity',
      'insecurity',
    ],
  },
  {
    field: 'equipment',
    inputField: 'equipment',
    kind: 'multiple',
    optional: true,
    values: ['phone_only', 'notebook', 'tablet', 'desktop'],
  },
  {
    field: 'monthly_income',
    inputField: 'monthlyIncome',
    kind: 'single',
    values: [
      'under_1000',
      'up_to_2500',
      '2500_to_5000',
      '5000_to_7000',
      'over_10000',
    ],
  },
  {
    field: 'clipper_experience',
    inputField: 'clipperExperience',
    kind: 'single',
    values: [
      'not_clipper',
      'one_campaign',
      'some_campaigns',
      'lives_from_clips',
    ],
  },
  {
    field: 'could_earn_more',
    inputField: 'couldEarnMore',
    kind: 'boolean',
    values: [false, true],
  },
  {
    field: 'financially_stuck',
    inputField: 'financiallyStuck',
    kind: 'boolean',
    values: [false, true],
  },
  {
    field: 'likes_current_job',
    inputField: 'likesCurrentJob',
    kind: 'boolean',
    values: [false, true],
  },
  {
    field: 'wants_remote_mobile_work',
    inputField: 'wantsRemoteMobileWork',
    kind: 'boolean',
    values: [true, false],
  },
  {
    field: 'self_employment_outlook',
    inputField: 'selfEmploymentOutlook',
    kind: 'single',
    values: ['dream', 'not_possible', 'already_self_employed_more_income'],
  },
  {
    field: 'first_30_day_revenue_outlook',
    inputField: 'first30DayRevenueOutlook',
    kind: 'single',
    values: ['yes', 'afraid', 'no'],
  },
] as const;

for (const question of questionnaire) {
  Object.freeze(question.values);
  Object.freeze(question);
}

export const VIRALIZE_QUESTIONNAIRE = Object.freeze(questionnaire);

export type ViralizeQualificationQuestion =
  (typeof VIRALIZE_QUESTIONNAIRE)[number];
export type ViralizeQualificationField = ViralizeQualificationQuestion['field'];
export type ViralizeQualificationInputField =
  ViralizeQualificationQuestion['inputField'];
export type ViralizeQualificationStage =
  | 'contact'
  | 'intro_video'
  | ViralizeQualificationField;

export type ViralizeQualificationQuestionForField<
  Field extends ViralizeQualificationField,
> = Extract<ViralizeQualificationQuestion, { field: Field }>;

export type ViralizeQualificationOptionForField<
  Field extends ViralizeQualificationField,
> = ViralizeQualificationQuestionForField<Field>['values'][number];

export type ViralizeQualificationStoredAnswerForField<
  Field extends ViralizeQualificationField,
> = ViralizeQualificationQuestionForField<Field> extends infer Question
  ? Question extends {
      kind: 'multiple';
      values: readonly (infer Option)[];
    }
    ? readonly Option[]
    : Question extends { values: readonly (infer Option)[] }
      ? Option
      : never
  : never;

export type ViralizeQualificationAnswer = {
  [Field in ViralizeQualificationField]:
    ViralizeQualificationStoredAnswerForField<Field>;
}[ViralizeQualificationField];

type ViralizeQualificationMutableAnswerForQuestion<
  Question extends ViralizeQualificationQuestion,
> = Question extends {
  kind: 'multiple';
  values: readonly (infer Option)[];
}
  ? Option[]
  : Question['values'][number];

type ViralizeQualificationAnswerInputForQuestion<
  Question extends ViralizeQualificationQuestion,
> = Question extends ViralizeQualificationQuestion
  ? {
      [InputField in Question['inputField']]:
        ViralizeQualificationMutableAnswerForQuestion<Question>;
    } & {
      [OtherInputField in Exclude<
        ViralizeQualificationInputField,
        Question['inputField']
      >]?: never;
    }
  : never;

/** Exactly one answer mutation field, correlated with its stored option type. */
export type ViralizeQualificationAnswerInput =
  ViralizeQualificationAnswerInputForQuestion<ViralizeQualificationQuestion>;

export function viralizeQualificationQuestionKey<
  Field extends ViralizeQualificationField,
>(
  field: Field,
): `viralizeQualification.questions.${Field}.question` {
  return `viralizeQualification.questions.${field}.question`;
}

export function viralizeQualificationOptionKey<
  Field extends ViralizeQualificationField,
  Option extends ViralizeQualificationOptionForField<Field>,
>(
  field: Field,
  value: Option,
): `viralizeQualification.questions.${Field}.options.${Option}` {
  return `viralizeQualification.questions.${field}.options.${String(value)}` as
    `viralizeQualification.questions.${Field}.options.${Option}`;
}
